import { useId, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  inclusionsFromLegacy,
  legacyFromInclusions,
  summarizeReviews,
  VENUE_STATUSES,
  type VenueInclusion,
  type Venue,
  type VenueReview,
  type VenueStatus,
} from '@guabi/shared';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Drawer } from '@/components/ui/Drawer';
import { Field, SelectInput, TextInput } from '@/components/ui/Field';
import { Pill } from '@/components/ui/Pill';
import { money } from '@/lib/format';
import { ApiRequestError } from '@/lib/api';
import { useCreateVenue, useDeleteVenue, useUpdateVenue } from './queries';
import { InclusionsEditor } from './InclusionsEditor';
import { Documents } from '@/features/documents/Documents';
import { ReviewForm } from './ReviewForm';
import { VenueNotes } from './VenueNotes';
import { Stars } from './Stars';

/**
 * A group of fields you open when you get to them.
 *
 * Open by default once it holds anything, shut while it is empty — so a venue
 * added from one field reads as a short page with obvious next steps rather
 * than fourteen blank inputs.
 */
function Section({
  title,
  filled,
  children,
}: {
  title: string;
  filled: boolean;
  children: React.ReactNode;
}) {
  // Frozen at mount. Passing `filled` straight through would make React rewrite
  // the attribute whenever it changed, so a section you deliberately closed
  // would spring open again the moment you typed into it.
  const [startOpen] = useState(filled);

  return (
    <details open={startOpen} className="border-t border-line pt-3">
      <summary className="eyebrow cursor-pointer list-none">
        {title}
        {!filled && <span className="ml-2 normal-case tracking-normal">&mdash;</span>}
      </summary>
      <div className="flex flex-col gap-4 pt-3">{children}</div>
    </details>
  );
}

export function VenueDrawer({
  wedId,
  venue,
  reviews,
  myUserId,
  currency,
  open,
  onClose,
  canWrite = true,
}: {
  wedId: string;
  /** Absent when adding. */
  venue?: Venue;
  reviews: VenueReview[];
  myUserId?: string;
  currency: string;
  open: boolean;
  onClose: () => void;
  /** A read-only member may open this to read it, and change nothing. */
  canWrite?: boolean;
}) {
  const { t } = useTranslation();
  const create = useCreateVenue(wedId);
  const update = useUpdateVenue(wedId);
  const remove = useDeleteVenue(wedId);

  const [name, setName] = useState(venue?.name ?? '');
  const [status, setStatus] = useState<VenueStatus>(venue?.status ?? 'found');
  const [website, setWebsite] = useState(venue?.website ?? '');
  const [instagram, setInstagram] = useState(venue?.instagram ?? '');
  const [capacity, setCapacity] = useState(venue?.capacity ? String(venue.capacity) : '');
  const [quote, setQuote] = useState(venue?.quoteCents ? String(venue.quoteCents / 100) : '');
  const [tourDate, setTourDate] = useState(venue?.tourDate ?? '');
  const [city, setCity] = useState(venue?.address?.city ?? '');
  const [line1, setLine1] = useState(venue?.address?.line1 ?? '');
  // Read through the legacy fallback, so a venue created before this feature
  // opens with its list already filled in from the old free-text chips.
  const [inclusions, setInclusions] = useState<VenueInclusion[]>(() =>
    venue ? inclusionsFromLegacy(venue) : [],
  );
  const [contactName, setContactName] = useState(venue?.contactName ?? '');
  const [contactEmail, setContactEmail] = useState(venue?.contactEmail ?? '');
  const [contactPhone, setContactPhone] = useState(venue?.contactPhone ?? '');
  const [error, setError] = useState<string>();
  // Stable per drawer, so the footer's button can name the form it submits.
  const formId = useId();
  const [confirming, setConfirming] = useState(false);

  const summary = summarizeReviews(reviews);
  const busy = create.isPending || update.isPending || remove.isPending;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(undefined);

    const address = {
      ...(line1 ? { line1 } : {}),
      ...(city ? { city } : {}),
    };

    const fields = {
      name,
      status,
      ...(website ? { website } : {}),
      instagram,
      // Money is integer cents everywhere; the input collects whole currency.
      ...(quote ? { quoteCents: Math.round(Number(quote) * 100) } : {}),
      ...(capacity ? { capacity: Number(capacity) } : {}),
      ...(tourDate ? { tourDate } : {}),
      ...(Object.keys(address).length > 0 ? { address } : {}),
      inclusions,
      // Written in step so anything still reading the old fields stays correct.
      ...legacyFromInclusions(inclusions),
      contactName,
      ...(contactEmail ? { contactEmail } : {}),
      contactPhone,
      currency,
    };

    try {
      if (venue) {
        await update.mutateAsync({
          venueId: venue.venueId,
          input: { ...fields, updatedAt: venue.updatedAt },
        });
      } else {
        await create.mutateAsync(fields);
      }
      onClose();
    } catch (err) {
      setError(
        err instanceof ApiRequestError && err.code === 'version_conflict'
          ? t('venues.conflict')
          : t('common.somethingWrong'),
      );
    }
  }

  async function onDelete() {
    if (!venue) return;
    setConfirming(false);
    try {
      await remove.mutateAsync({ venueId: venue.venueId });
      onClose();
    } catch {
      setError(t('common.somethingWrong'));
    }
  }

  return (
    <Drawer
      open={open}
      title={venue ? venue.name : t('venues.addTitle')}
      onClose={onClose}
      /*
       * Save lives here, in the pinned footer, not inline in the form.
       *
       * It used to sit halfway down a long scroll, with Documents and then the
       * whole reviews section — including the review's own save button — below
       * it. So saving a venue meant scrolling past the button you wanted to a
       * different button that looked the same, which is how people end up
       * writing a review when they meant to change the capacity.
       *
       * `form` ties a button outside the form to it, which is what HTML has that
       * attribute for and the only reason this can move at all.
       */
      footer={
        <div className="flex flex-wrap items-center gap-3">
          {canWrite && (
            <Button type="submit" form={formId} loading={busy}>
              {t('common.save')}
            </Button>
          )}
          <Button type="button" variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          {venue && summary.average !== null && (
            <span className="ml-auto text-caption text-leaf-soft">
              {t('venues.coupleAverage', { average: summary.average, count: summary.count })}
            </span>
          )}
        </div>
      }
    >
      <div className="flex flex-col gap-6">
        {/* First thing on the page and the only one that saves itself, because
            it is the field that actually gets used. */}
        {venue && <VenueNotes wedId={wedId} venue={venue} canWrite={canWrite} />}

        <form id={formId} onSubmit={onSubmit} className="flex flex-col gap-4">
          {/* One attribute instead of thirty: a disabled fieldset disables every
            control inside it, so a read-only member reads the real values and
            can change none of them. `contents` keeps it out of the layout. */}
          <fieldset disabled={!canWrite} className="contents">
            <Field label={t('venues.name')}>
              {({ id }) => (
                <TextInput
                  id={id}
                  required
                  maxLength={200}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              )}
            </Field>

            <Field label={t('venues.status')}>
              {({ id }) => (
                <SelectInput
                  id={id}
                  value={status}
                  onChange={(e) => setStatus(e.target.value as VenueStatus)}
                >
                  {VENUE_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {t(`venues.statusValue.${s}`)}
                    </option>
                  ))}
                </SelectInput>
              )}
            </Field>

            <Section
              title={t('venues.sectionMoney')}
              filled={Boolean(quote || capacity || tourDate)}
            >
              <div className="flex flex-wrap gap-3">
                <Field label={t('venues.quote')} hint={currency}>
                  {({ id, describedBy }) => (
                    <TextInput
                      id={id}
                      aria-describedby={describedBy}
                      type="number"
                      min={0}
                      step="0.01"
                      value={quote}
                      onChange={(e) => setQuote(e.target.value)}
                    />
                  )}
                </Field>
                <Field label={t('venues.capacity')}>
                  {({ id }) => (
                    <TextInput
                      id={id}
                      type="number"
                      min={0}
                      value={capacity}
                      onChange={(e) => setCapacity(e.target.value)}
                    />
                  )}
                </Field>
              </div>

              <Field label={t('venues.tourDate')}>
                {({ id }) => (
                  <TextInput
                    id={id}
                    type="date"
                    value={tourDate}
                    onChange={(e) => setTourDate(e.target.value)}
                  />
                )}
              </Field>
            </Section>

            <Section title={t('venues.whatsIncluded')} filled={inclusions.length > 0}>
              <div className="flex flex-col gap-2">
                <p className="text-caption text-leaf-soft">{t('venues.whatsIncludedHint')}</p>
                <InclusionsEditor value={inclusions} currency={currency} onChange={setInclusions} />
              </div>
            </Section>

            <Section
              title={t('venues.sectionWhere')}
              filled={Boolean(line1 || city || website || instagram)}
            >
              <Field label={t('venues.line1')}>
                {({ id }) => (
                  <TextInput
                    id={id}
                    maxLength={200}
                    value={line1}
                    onChange={(e) => setLine1(e.target.value)}
                  />
                )}
              </Field>
              <Field label={t('venues.city')}>
                {({ id }) => (
                  <TextInput
                    id={id}
                    maxLength={200}
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                  />
                )}
              </Field>

              <Field label={t('venues.website')}>
                {({ id }) => (
                  <TextInput
                    id={id}
                    type="url"
                    placeholder="https://"
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                  />
                )}
              </Field>
              <Field label={t('venues.instagram')}>
                {({ id }) => (
                  <TextInput
                    id={id}
                    maxLength={40}
                    value={instagram}
                    onChange={(e) => setInstagram(e.target.value)}
                  />
                )}
              </Field>
            </Section>

            <Section
              title={t('venues.sectionContact')}
              filled={Boolean(contactName || contactEmail || contactPhone)}
            >
              <Field label={t('venues.contactName')}>
                {({ id }) => (
                  <TextInput
                    id={id}
                    maxLength={200}
                    value={contactName}
                    onChange={(e) => setContactName(e.target.value)}
                  />
                )}
              </Field>
              <div className="flex flex-wrap gap-3">
                <Field label={t('venues.contactEmail')}>
                  {({ id }) => (
                    <TextInput
                      id={id}
                      type="email"
                      value={contactEmail}
                      onChange={(e) => setContactEmail(e.target.value)}
                    />
                  )}
                </Field>
                <Field label={t('venues.contactPhone')}>
                  {({ id }) => (
                    <TextInput
                      id={id}
                      type="tel"
                      maxLength={40}
                      value={contactPhone}
                      onChange={(e) => setContactPhone(e.target.value)}
                    />
                  )}
                </Field>
              </div>
            </Section>

            {error && (
              <p role="alert" className="text-body text-status-declinedFg">
                {error}
              </p>
            )}

            {/* Only the destructive one stays inline. Deleting a venue is not
              something to put a thumb's width from Save, and at the bottom of
              the form is where you have already read what you are deleting. */}
            {venue && (
              <div className="flex flex-wrap gap-3 pt-2">
                <Button type="button" variant="danger" onClick={() => setConfirming(true)}>
                  {t('venues.remove')}
                </Button>
              </div>
            )}
          </fieldset>
        </form>

        {venue && (
          <section className="flex flex-col gap-3 border-t border-line pt-5">
            <Documents wedId={wedId} venueId={venue.venueId} />
          </section>
        )}

        {venue && (
          <section className="flex flex-col gap-3 border-t border-line pt-5">
            {/* The gap belongs beside the reviews it is about. It was in the
                footer, where it sat next to Save and read like a warning about
                the thing you were saving. */}
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h3 className="text-section">{t('venues.reviews')}</h3>
              {summary.agreementGap !== null && summary.agreementGap > 0 && (
                <Pill tone="maybe">{t('venues.agreementGap', { gap: summary.agreementGap })}</Pill>
              )}
            </div>
            {summary.average === null && (
              <p className="text-caption text-leaf-soft">{t('venues.noReviews')}</p>
            )}

            {summary.dealbreakers.length > 0 && (
              <div className="panel flex flex-col gap-1 p-3" role="note">
                <span className="text-caption font-medium text-status-declinedFg">
                  {t('venues.dealbreakers')}
                </span>
                {summary.dealbreakers.map((d) => (
                  <span key={d} className="text-body text-leaf-soft">
                    {d}
                  </span>
                ))}
              </div>
            )}

            {reviews.length > 0 && (
              <ul className="flex flex-col gap-3">
                {reviews.map((r) => (
                  <li key={r.userId} className="flex flex-col gap-1 border-b border-line pb-2">
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="text-body">
                        {r.reviewerName ||
                          (r.userId === myUserId ? t('venues.you') : t('venues.member'))}
                      </span>
                      <Stars value={r.stars} />
                      {r.wouldBook === true && (
                        <Pill tone="attending">{t('venues.wouldBook')}</Pill>
                      )}
                      {r.wouldBook === false && (
                        <Pill tone="declined">{t('venues.wouldNotBook')}</Pill>
                      )}
                    </span>
                    {r.liked && (
                      <span className="text-caption text-leaf-soft">
                        {t('venues.liked')}: {r.liked}
                      </span>
                    )}
                    {r.disliked && (
                      <span className="text-caption text-leaf-soft">
                        {t('venues.disliked')}: {r.disliked}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}

            <ReviewForm
              wedId={wedId}
              venueId={venue.venueId}
              mine={reviews.find((r) => r.userId === myUserId)}
            />

            {venue.quoteCents !== undefined && (
              <p className="text-caption text-leaf-soft">
                {t('venues.quote')}: {money(venue.quoteCents, currency)}
              </p>
            )}
          </section>
        )}
      </div>

      <ConfirmDialog
        open={confirming}
        title={t('venues.removeTitle', { name: venue?.name ?? '' })}
        body={
          <>
            {summary.count > 0 && <p>{t('venues.removeAlsoReviews', { count: summary.count })}</p>}
            <p>{t('common.cannotBeUndone')}</p>
          </>
        }
        confirmLabel={t('common.remove')}
        busy={remove.isPending}
        onConfirm={() => void onDelete()}
        onClose={() => setConfirming(false)}
      />
    </Drawer>
  );
}
